import {
  AriaAttributes,
  type RefObject,
  type FormEvent as ReactFormEvent,
  type ChangeEvent,
  type FocusEvent,
} from 'react';
import { useCallback, useId, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Text } from '@dynatrace/strato-components/typography';

import { useFormFieldMessagesContext } from './useFormFieldMessagesContext.js';
import { FormFieldMessagesAction } from '../state/formfield-messages-state-reducer.js';

/**
 * Getter function to read the error type form the validity state.
 * The validity state values are readonly and can't therefore not be iterated but need to accessed directly instead.
 * https://stackoverflow.com/questions/20891950/html-validation-api-returns-an-empty-object-in-firefox?rq=2
 * @internal
 */
export function getErrorType(validityState?: ValidityState) {
  if (validityState) {
    if (validityState.badInput) {
      return 'badInput';
    }
    if (validityState.patternMismatch) {
      return 'patternMismatch';
    }
    if (validityState.rangeOverflow) {
      return 'rangeOverflow';
    }
    if (validityState.rangeUnderflow) {
      return 'rangeUnderflow';
    }
    if (validityState.stepMismatch) {
      return 'stepMismatch';
    }
    if (validityState.tooLong) {
      return 'tooLong';
    }
    if (validityState.tooShort) {
      return 'tooShort';
    }
    if (validityState.typeMismatch) {
      return 'typeMismatch';
    }
    if (validityState.valueMissing) {
      return 'valueMissing';
    }
    /** We don't check for custom error since those should never be automatically updated. */
  }
  return null;
}

/**
 * Internal getter function to return the matching formatted message for
 * each validityStateKey.
 * @internal
 */
export function getI18nErrorMessage(
  key: keyof ValidityState,
  ref?: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null,
) {
  switch (key) {
    case 'badInput':
      return (
        <FormattedMessage
          id="/cwiOkubC69MZmRu"
          defaultMessage="Please provide input in a valid format."
          description="Error message for input that the browser is unable to convert, e.g. string for a number input."
        />
      );
    case 'patternMismatch':
      return (
        <FormattedMessage
          id="qPeX7AgKbKzBFqGZ"
          defaultMessage="Please match the format requested."
          description="Error message for input that doesn't follow the requested pattern."
        />
      );
    case 'rangeOverflow':
      return (
        <FormattedMessage
          id="14mVeqEFpqD2fpev"
          defaultMessage="Value must be less or equal to {max}."
          description="Error message for input that value is higher then the max."
          values={{ max: ref?.getAttribute('max') }}
        />
      );
    case 'rangeUnderflow':
      return (
        <FormattedMessage
          id="5XoVjJUQ5b86MLW+"
          defaultMessage="Value must be higher or equal to {min}."
          description="Error message for input that value is lower then the max."
          values={{ min: ref?.getAttribute('min') }}
        />
      );
    case 'stepMismatch':
      return (
        <FormattedMessage
          id="mE/S6XqlsZc0mYiH"
          defaultMessage="Please provide a value in a multitude of {step}."
          description="Error message for input that isn't a multitude of the provided step."
          values={{ step: ref?.getAttribute('step') }}
        />
      );
    case 'tooLong':
      return (
        <FormattedMessage
          id="JOIA3jQfIIkgnW04"
          defaultMessage="Please shorten this text to {maxLength} characters or less."
          description="Error message for input that is shorter than the maxLength."
          values={{ maxLength: ref?.getAttribute('maxLength') }}
        />
      );
    case 'tooShort':
      return (
        <FormattedMessage
          id="sJeuQTGrzcMvd6fY"
          defaultMessage="Please lengthen this text to {minLength} characters or more."
          description="Error message for input that is shorter than the minLength."
          values={{ minLength: ref?.getAttribute('minLength') }}
        />
      );
    case 'typeMismatch':
      return (
        <FormattedMessage
          id="fLfhKO8cXlo3vW5M"
          defaultMessage="Please provide a value that matches the requested type ({type})."
          description="Error message for input that doesn't match the expected type."
          values={{ type: ref?.getAttribute('type') }}
        />
      );
    case 'valueMissing':
      return (
        <FormattedMessage
          id="laIylhVFbIvEZrxt"
          defaultMessage="Please fill in this field."
          description="Error message for missing input."
        />
      );

    case 'customError':
    case 'valid':
    default:
      /** Shouldn't be reached */
      return null;
  }
}

/** @internal */
interface FormFieldValidationProps {
  ariaProps: Pick<
    AriaAttributes,
    'aria-errormessage' | 'aria-invalid' | 'aria-describedby'
  >;
  onInvalid: (
    event: ReactFormEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  onChange: (
    event?: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  onBlur: (
    event?: FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
}

/** @internal */
export function useFormFieldValidation(
  ref: RefObject<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null
  >,
  controlId?: string,
): FormFieldValidationProps {
  const { state, dispatch } = useFormFieldMessagesContext();
  const errorId = useId();

  const ariaProps = useMemo(
    () => ({
      'aria-errormessage': state.showError
        ? [...state.customMessages, ...state.formControlMessages]
            .filter((msg) => msg.variant === 'error')
            .map((msg) => msg.id)
            .join()
        : undefined,
      'aria-invalid': state.showError,
      'aria-describedby': [
        ...state.customMessages,
        ...state.formControlMessages,
      ]
        .map((msg) => msg.id)
        .join(),
    }),
    [state.customMessages, state.formControlMessages, state.showError],
  );

  const onInvalid = useCallback(
    (
      event: ReactFormEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const errorType = getErrorType(ref.current?.validity);

      if (errorType) {
        dispatch?.({
          type: FormFieldMessagesAction.ADDMESSAGE,
          payload: {
            id: errorId,
            message: (
              <Text textStyle="small" as="p">
                {getI18nErrorMessage(errorType, ref.current)}
              </Text>
            ),
            validityStateKey: errorType,
            messageType: 'formcontrol',
            variant: 'error',
            controlId,
          },
        });
      }

      dispatch?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: true },
      });

      // Prevents the browser validation pop up.
      event.preventDefault();
    },
    [controlId, dispatch, errorId, ref],
  );

  const onBlur = useCallback(() => {
    ref.current?.reportValidity();
    dispatch?.({
      type: FormFieldMessagesAction.SETISTOUCHED,
      payload: { isTouched: true },
    });
  }, [dispatch, ref]);

  const onChange = useCallback(() => {
    const errorType = getErrorType(ref.current?.validity);

    state.formControlMessages.forEach((message) => {
      if (
        message.validityStateKey !== errorType &&
        message.validityStateKey !== 'customError'
      ) {
        dispatch?.({
          type: FormFieldMessagesAction.REMOVEMESSAGE,
          payload: {
            id: message.id,
            messageType: 'formcontrol',
            controlId,
          },
        });
      }
    });

    if (errorType) {
      dispatch?.({
        type: FormFieldMessagesAction.ADDMESSAGE,
        payload: {
          id: errorId,
          message: (
            <Text textStyle="small" as="p">
              {getI18nErrorMessage(errorType, ref.current)}
            </Text>
          ),
          validityStateKey: errorType,
          messageType: 'formcontrol',
          variant: 'error',
          controlId,
        },
      });
    }
  }, [controlId, dispatch, errorId, ref, state.formControlMessages]);

  return {
    ariaProps,
    onInvalid,
    onChange,
    onBlur,
  };
}
